<template>
    <v-container fluid>
        <v-row align="center" justify="space-between">
            <v-col cols="12" md="8">
                <h1>Theme Preview</h1>
                <p>Quick visual playground for palettes, shades and common components.</p>
            </v-col>
            <v-col cols="12" md="4" class="text-right">
                <theme-selector />
                <v-btn class="ml-3" @click="toggleDark" small>
                    {{ $vuetify.theme.dark ? 'Dark' : 'Light' }}
                </v-btn>
            </v-col>
        </v-row>

        <!-- Top: Palette and Generated Shades -->
        <v-row>
            <v-col cols="12">
                <v-card>
                    <v-card-title>Palette (current)</v-card-title>
                    <v-card-text>
                        <v-row v-for="color in allColors" :key="color" class="palette-grid" dense>
                            <v-col v-for="shade in shadeSuffixes" :key="shade" cols="6" sm="4" md="3" lg="2">
                                <div class="palette-item">
                                    <div class="palette-swatch-large" :class="color + (shade ? ' ' + shade : '') + ' on-' + color + '--text'">
                                        <div class="palette-sample">{{ color + (shade ? ' ' + shade : '') }}</div>
                                    </div>
                                    <div class="palette-meta">
                                        <div class="palette-name">{{ key }}</div>
                                        <div class="palette-hex">{{ displayPaletteColor(key) }}</div>
                                    </div>
                                </div>
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12">
                <v-card>
                    <v-card-title>Basic colors</v-card-title>
                    <v-card-text>
                        <v-row class="palette-grid" dense>
                            <v-col v-for="key in paletteKeys" :key="key" cols="6" sm="4" md="3" lg="2">
                                <div class="palette-item">
                                    <div class="palette-swatch-large"
                                        :style="{ backgroundColor: displayPaletteColor(key), color: getOnColor(key) }">
                                        <div class="palette-sample">Aa</div>
                                    </div>
                                    <div class="palette-meta">
                                        <div class="palette-name">{{ key }}</div>
                                        <div class="palette-hex">{{ displayPaletteColor(key) }}</div>
                                    </div>
                                </div>
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <v-row class="mt-4">
            <v-col cols="12">
                <v-card>
                    <v-card-title>Generated Shades</v-card-title>
                    <v-card-text>
                        <div v-for="name in shadeNames" :key="name" class="shade-block">
                            <h4 class="mb-2">{{ name }}</h4>
                            <div class="shade-row">
                                <div v-for="suffix in shadeSuffixes" :key="suffix"
                                    :class="['shade-cell', { 'shade-base': suffix === '' }]">
                                    <div class="shade-swatch" :style="{ backgroundColor: getShadeColor(name, suffix) }">
                                    </div>
                                    <div class="shade-label">{{ shadeVarLabel(name, suffix) }}</div>
                                    <div class="shade-hex">{{ getShadeColor(name, suffix) }}</div>
                                </div>
                            </div>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <!-- Bottom: Components Preview -->
        <v-row class="mt-6">
            <v-col cols="12">
                <v-card>
                    <v-card-title>Components Preview</v-card-title>
                    <v-card-text>
                        <v-row>
                            <v-col cols="12" md="6">
                                <v-btn color="primary" class="ma-2">Primary</v-btn>
                                <v-btn color="secondary" class="ma-2">Secondary</v-btn>
                                <v-btn color="accent" class="ma-2">Accent</v-btn>
                                <v-chip color="primary" class="ma-2" dark>Chip primary</v-chip>
                                <v-chip color="secondary" class="ma-2" dark>Chip secondary</v-chip>
                                <v-chip color="accent" class="ma-2" dark>Chip accent</v-chip>
                                <v-chip color="red" class="ma-2" dark>Red</v-chip>

                                <!-- Chips gallery: multiple colors and variants -->
                                <div class="mt-3">
                                    <div class="mb-2"><strong>Chips gallery</strong></div>
                                    <v-row>
                                        <v-col cols="12">
                                            <div v-for="group in chipGroups" :key="group.name" class="mb-3">
                                                <div class="chip-group-title">{{ group.name }}</div>
                                                <div class="chip-list">
                                                    <v-chip v-for="variant in chipVariants"
                                                        :key="group.name + variant.key" :color="group.color"
                                                        :outlined="variant.outlined" :small="variant.small"
                                                        :pill="variant.pill" :disabled="variant.disabled"
                                                        :closable="variant.closable" :text-color="variant.textColor"
                                                        class="ma-1">
                                                        {{ variant.label }}
                                                    </v-chip>
                                                </div>
                                            </div>
                                        </v-col>
                                    </v-row>
                                </div>
                            </v-col>

                            <v-col cols="12" md="6">
                                <v-text-field label="Text field" placeholder="Placeholder"></v-text-field>
                                <v-switch label="Switch" v-model="switchVal"></v-switch>
                                <v-checkbox label="Checkbox" v-model="checkboxVal"></v-checkbox>
                            </v-col>

                            <v-col cols="12">
                                <v-tabs>
                                    <v-tab>One</v-tab>
                                    <v-tab>Two</v-tab>
                                    <v-tab>Three</v-tab>
                                </v-tabs>
                                <v-card class="mt-3 pa-3">
                                    <p>This is a sample card to preview surface/background texting and elevation.</p>
                                    <v-list dense>
                                        <v-list-item>
                                            <v-list-item-content>
                                                <v-list-item-title>List item</v-list-item-title>
                                                <v-list-item-subtitle>Subtitle</v-list-item-subtitle>
                                            </v-list-item-content>
                                        </v-list-item>
                                    </v-list>
                                </v-card>
                            </v-col>

                        </v-row>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import ThemeSelector from '@/components/ThemeSelector'
import { getTheme } from '@/plugins/themes'

export default {
    name: 'ThemePreview',
    components: { ThemeSelector },
    data() {
        return {
            switchVal: false,
            checkboxVal: false,
            paletteKeys: ['primary', 'secondary', 'accent', 'surface', 'background', 'drawer', 'drawerSelection', 'error', 'info', 'success', 'warning'],
            basicColors: ['red', 'blue', 'green', 'orange', 'yellow', 'teal', 'cyan'],
            shadeSuffixes: ['', 'lighten-1', 'lighten-2', 'lighten-3', 'lighten-4', 'lighten-5', 'darken-1', 'darken-2', 'darken-3', 'darken-4', 'accent-1', 'accent-2', 'accent-3', 'accent-4'],
            chipGroups: [
                { name: 'Primary', color: 'primary' },
                { name: 'Secondary', color: 'secondary' },
                { name: 'Accent', color: 'accent' },
                { name: 'Red', color: 'red' },
                { name: 'Blue', color: 'blue' },
                { name: 'Green', color: 'green' },
                { name: 'Orange', color: 'orange' },
                { name: 'Yellow', color: 'yellow' },
                { name: 'Teal', color: 'teal' },
                { name: 'Cyan', color: 'cyan' },
                { name: 'Error', color: 'error' },
                { name: 'Info', color: 'info' },
                { name: 'Success', color: 'success' },
                { name: 'Warning', color: 'warning' },
            ],
            chipVariants: [
                { key: 'default', label: 'Default', outlined: false, small: false, pill: false, disabled: false, closable: false, textColor: null },
                { key: 'outlined', label: 'Outlined', outlined: true, small: false, pill: false, disabled: false, closable: false, textColor: null },
                { key: 'small', label: 'Small', outlined: false, small: true, pill: false, disabled: false, closable: false, textColor: null },
                { key: 'pill', label: 'Pill', outlined: false, small: false, pill: true, disabled: false, closable: false, textColor: null },
                { key: 'disabled', label: 'Disabled', outlined: false, small: false, pill: false, disabled: true, closable: false, textColor: null },
                { key: 'closable', label: 'Closable', outlined: false, small: false, pill: false, disabled: false, closable: true, textColor: null },
                { key: 'filter', label: 'Filter', outlined: false, small: false, pill: false, disabled: false, closable: false, textColor: null },
                { key: 'customText', label: 'Custom text', outlined: false, small: false, pill: false, disabled: false, closable: false, textColor: '#000' },
            ],
        }
    },
    computed: {
        currentThemeKey() { return this.$store.state.user.themeKey },
        currentMode() { return this.$vuetify.theme.dark ? 'dark' : 'light' },
        currentTheme() { return getTheme(this.currentThemeKey)[this.currentMode] },
        allColors() {
            return this.basicColors.concat(this.paletteKeys)
        },
    },
    methods: {
        toggleDark() { this.$vuetify.theme.dark = !this.$vuetify.theme.dark },
        cssVar(name) {
            const v = getComputedStyle(document.documentElement).getPropertyValue(name)
            return v ? v.trim() : ''
        },
        // Try multiple variable name candidates and return first non-empty (kept for edge cases)
        resolveVar(candidates) {
            for (let i = 0; i < candidates.length; i++) {
                const v = this.cssVar(candidates[i])
                if (v) return v
            }
            return ''
        },
        shadeVar(name, suffix) {
            return suffix ? `--color-${name}-${suffix}` : `--color-${name}`
        },
        shadeVarLabel(name, suffix) {
            return suffix ? `${name} ${suffix}` : `${name} base`
        },
        // Attempt to get a color for a shade: prefer values from Vuetify theme object (native),
        // then theme object values. Do NOT compute shades.
        getShadeColor(name, suffix) {
            const mode = this.currentMode
            // 1) Check Vuetify theme object for an explicit key (native approach)
            const vuetifyTheme = (this.$vuetify && this.$vuetify.theme && this.$vuetify.theme.themes) ? this.$vuetify.theme.themes[mode] || {} : {}
            if (suffix && suffix !== '') {
                // try keys like 'primary-lighten-1' or 'primary_lighten_1' if present in theme object
                const candidateKeys = [`${name}-${suffix}`, `${name}_${suffix}`]
                for (const k of candidateKeys) if (vuetifyTheme[k]) return vuetifyTheme[k]
            } else {
                if (vuetifyTheme[name]) return vuetifyTheme[name]
            }

            // 2) Fallback to themes.js supplied theme object for current mode
            if (this.currentTheme) {
                if (suffix && suffix !== '') {
                    const candidateKeys = [`${name}-${suffix}`, `${name}_${suffix}`]
                    for (const k of candidateKeys) if (this.currentTheme[k]) return this.currentTheme[k]
                } else if (this.currentTheme[name]) return this.currentTheme[name]
            }

            // 3) No explicit value available — do not compute or invent shades
            return ''
        },
        // return on-color for a palette key, prefer exported --on-<key>, fallback to computed contrast
        getOnColor(key) {
            const mode = this.currentMode
            const vuetifyTheme = (this.$vuetify && this.$vuetify.theme && this.$vuetify.theme.themes) ? this.$vuetify.theme.themes[mode] || {} : {}
            if (vuetifyTheme[`on-${key}`]) return vuetifyTheme[`on-${key}`]
            if (this.currentTheme && this.currentTheme[`on-${key}`]) return this.currentTheme[`on-${key}`]
            return '#000'
        },

        // Return a palette color from CSS variables, Vuetify vars or current theme
        displayPaletteColor(key) {
            const mode = this.currentMode
            const vuetifyTheme = (this.$vuetify && this.$vuetify.theme && this.$vuetify.theme.themes) ? this.$vuetify.theme.themes[mode] || {} : {}
            if (vuetifyTheme && vuetifyTheme[key]) return vuetifyTheme[key]
            if (this.currentTheme && this.currentTheme[key]) return this.currentTheme[key]
            // last resort: CSS variables (kept for edge cases)
            const candidates = [`--${key}`, `--color-${key}`, `--v-${key}-base`, `--v-${key}`]
            const found = this.resolveVar(candidates)
            if (found) return found
            return ''
        },
        // color math now lives in src/utils/colorUtils.js
    },
    mounted() {
        // ensure CSS vars exist for current theme
        // ThemeSelector.applyTheme mounted earlier should have set vars; if not, set them now
        // apply directly from themes.js
        const theme = getTheme(this.currentThemeKey)
        const mode = this.currentMode
        if (theme && theme[mode]) {
            const colors = theme[mode]
            Object.keys(colors).forEach(k => {
                try { document.documentElement.style.setProperty(`--${k}`, colors[k]) } catch (e) { /* ignore */ }
                if (!k.startsWith('on-')) {
                    try { document.documentElement.style.setProperty(`--color-${k}`, colors[k]) } catch (e) { /* ignore */ }
                }
            })
        }
    }
}
</script>

<style scoped>
.swatch-row {
    display: flex;
    align-items: center
}

.swatch {
    width: 40px;
    height: 40px;
    border-radius: 6px;
    border: 1px solid rgba(0, 0, 0, 0.06);
    margin-right: 12px
}

.swatch-info {
    display: flex;
    flex-direction: column
}

.swatch-name {
    font-weight: 600
}

.shade-row {
    display: flex;
    flex-wrap: wrap;
    gap: 10px
}

.shade-cell {
    width: 110px;
    text-align: center
}

.shade-swatch {
    height: 44px;
    border-radius: 4px;
    border: 1px solid rgba(0, 0, 0, 0.06)
}

.shade-label {
    font-size: 12px;
    margin-top: 6px
}

.shade-hex {
    font-size: 11px;
    color: var(--details, #666);
}

.chip-group-title {
    font-weight: 700;
    margin-bottom: 6px
}

.chip-list {
    display: flex;
    flex-wrap: wrap;
    align-items: center
}

.chip-row {
    display: flex;
    align-items: center;
    gap: 12px
}

.shade-base {
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.06) inset;
    border-radius: 6px
}

.shade-cell.shade-base .shade-label {
    font-weight: 700
}

.shade-cell.shade-base .shade-hex {
    font-weight: 600
}

.palette-grid {
    margin: 0 -12px
}

.palette-item {
    display: flex;
    flex-direction: column;
    align-items: flex-start
}

.palette-swatch-large {
    width: 100%;
    height: 84px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    margin-bottom: 8px
}

.palette-sample {
    font-weight: 700;
    font-size: 18px
}

.palette-meta {
    display: flex;
    flex-direction: column
}

.palette-name {
    font-weight: 600;
    text-transform: capitalize
}

.palette-hex {
    font-size: 12px;
    color: var(--details, #666)
}
</style>
